<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-3 items-center flex-shrink-0 sm:pr-10">
        <div class="flex-grow flex items-center">
          <div class="text-13 font-600">{{ $t('navigation.flows') }}</div>
        </div>
        <div class="ml-auto flex flex-row justify-center">
          <PillButton :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
      </div>

      <div class="w-full flex-grow overflow-hidden pb-2">
        <TableElevated :items="flowList" :headers="flowHeaders" class="text-13 pr-3 sm:pr-10">
          <template v-slot:[`item.name`]="{ value }">
            <span class="text-13 font-500 truncate">{{ value }}</span>
          </template>
          <template v-slot:[`item.tags`]="{ item }">
            <span v-if="item.flowType === 'Agent'" class="text-10 font-600 px-1.5 py-0.5 rounded-4 bg-purple-100 text-purple-700">Agent</span>
            <span v-else class="text-10 font-600 px-1.5 py-0.5 rounded-4 bg-blue-100 text-blue-700">Flow</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="flex flex-row justify-end items-center ml-auto">
              <IconButton icon="edit" class="mr-1 text-primary" @click="onEditClicked(item)" />
              <IconButton class="text-primary" icon="delete" @click="onDeleteClicked(item)" />
            </div>
          </template>
        </TableElevated>
      </div>
    </div>

    <Modal v-if="loading">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { sortByKey } from '@/helpers/array';
import DeleteModal from '../../modals/DeleteModal.vue';
import CreateProjectFlow from '../../project/CreateProjectFlow.vue';
import AgentEditorModal from '../../flow/AgentEditorModal.vue';

export default {
  name: 'Flows',
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectFlows']),
    projectId() {
      return this.$route.params.projectId;
    },
    flowList() {
      return sortByKey(Object.values(this.getProjectFlows()), 'name');
    },
    flowHeaders() {
      return [
        { key: 'name', title: this.$t('name') },
        { key: 'tags', title: this.$t('tags'), align: 'center', width: '110px' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectFlows', 'deleteProjectFlow']),
    onCreateClicked() {
      this.$showModal(
        CreateProjectFlow,
        {},
        {
          save: (response) => {
            if (response.flowType === 'Agent') {
              // New agents are configured in the modal first, then land in the designer.
              this.openAgentEditor(response.flow_id, { name: 'project-agent', params: { projectId: this.projectId, flowId: response.flow_id } });
            } else {
              this.$router.push({ name: 'project-flow', params: { projectId: this.projectId, flowId: response.flow_id } });
            }
          },
        },
      );
    },
    onEditClicked(flow) {
      if (flow.flowType === 'Agent') {
        // Existing agents open in the view-only designer; the designer's Edit
        // button switches to the AgentEditorModal.
        this.$router.push({ name: 'project-agent', params: { projectId: this.projectId, flowId: flow.flow_id } });
      } else {
        this.$router.push({ name: 'project-flow', params: { projectId: this.projectId, flowId: flow.flow_id } });
      }
    },
    openAgentEditor(flowId, routeOnSave = null) {
      this.$showModal(AgentEditorModal, { flowId, projectId: this.projectId }, routeOnSave ? { saved: () => this.$router.push(routeOnSave) } : undefined);
    },
    onDeleteClicked(flow) {
      this.$showModal(DeleteModal, {
        title: this.$t('confirm'),
        subtitle: this.$t('projects.flows.confirm_remove_message'),
        info: this.$t('all_data_will_be_removed'),
        onConfirm: async () => {
          await this.deleteProjectFlow({ project_id: this.projectId, flow_id: flow.flow_id });
        },
      });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.loading = true;
        await this.fetchProjectFlows({ projectId: this.projectId });
        this.loading = false;
      },
      immediate: true,
    },
  },
};
</script>
